import React, { useState } from "react";
import { toast } from "react-toastify";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import * as hrmsService from "../../services/hrmsService";
import { HR_PERMISSIONS, hasHrPermission } from "../../constants/hrPermissions";
import { openSignedUrl } from "./EmployeeDocuments";
import { FormFields, cleanValues } from "./hrForm";
import { EmptyState, Field, LoadState, PageHeader, PrimaryButton, SecondaryButton, Tabs } from "./hrComponents";
import { useHr } from "./HrGate";
import { cardStyle, formatDate, formatDateTime, iconBtnStyle, pageStyle, statusBadge, statusLabel, tdStyle, thStyle, useLoad } from "./hrUi";

const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };

/* --------------------------- company documents (DOC-05) --------------------------- */

function CompanyUpload({ show, onHide, onDone }) {
  const [values, setValues] = useState({ requires_ack: false });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  React.useEffect(() => {
    if (show) {
      setValues({ requires_ack: false });
      setFile(null);
    }
  }, [show]);
  const save = async () => {
    if (!file) return toast.error("Choose a file to upload");
    setSaving(true);
    try {
      const res = await hrmsService.uploadCompanyDocument(file, cleanValues({ ...values, requires_ack: values.requires_ack ? "true" : "false" }));
      if (res?.success) {
        toast.success("Uploaded. Publish it when it is ready for everyone.");
        onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };
  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title="Upload company document"
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving || !values.title}>
            Upload
          </PrimaryButton>
        </>
      }
    >
      <FormFields
        values={values}
        onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))}
        fields={[
          { key: "title", label: "Title", required: true, max: 150 },
          { key: "description", label: "Description", type: "textarea", max: 500, rows: 2 },
          { key: "requires_ack", label: "Employees must acknowledge they have read it", type: "checkbox" },
        ]}
      />
      <Field label="File" required hint="PDF, JPG, PNG or DOCX up to 10 MB">
        <input type="file" className="form-control" accept=".pdf,.jpg,.jpeg,.png,.docx" onChange={(e) => setFile(e.target.files[0] || null)} />
      </Field>
    </SlidePanel>
  );
}

function Acknowledgements({ doc, onHide }) {
  const { data, loading, error, reload } = useLoad(() => hrmsService.getAcknowledgements(doc.id), [doc?.id], { enabled: !!doc });
  return (
    <SlidePanel show={!!doc} onHide={onHide} title={doc ? `Acknowledgements: ${doc.title}` : ""} footer={<SecondaryButton onClick={onHide}>Close</SecondaryButton>}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {data ? (
          <>
            <div style={{ fontSize: 14, marginBottom: 12 }}>
              {data.acknowledged} of {data.total} have acknowledged
            </div>
            {data.people.map((p) => (
              <div key={p.employee_id} className="d-flex justify-content-between py-2" style={{ borderBottom: "1px solid #F3F4F6", fontSize: 14 }}>
                <span>
                  {p.full_name} <span style={{ color: "#6B7280", fontSize: 12 }}>{p.employee_code}</span>
                </span>
                <span style={{ color: p.acknowledged ? "#065F46" : "#92400E" }}>{p.acknowledged ? formatDateTime(p.acknowledged_at) : "Not yet"}</span>
              </div>
            ))}
          </>
        ) : null}
      </LoadState>
    </SlidePanel>
  );
}

function CompanyDocuments({ isAdmin }) {
  const { data, loading, error, reload } = useLoad(() => hrmsService.listCompanyDocuments(), []);
  const [uploading, setUploading] = useState(false);
  const [acks, setAcks] = useState(null);
  const rows = data || [];

  const run = async (fn, okText) => {
    const res = await fn().catch((e) => ({ success: false, message: e.message }));
    if (res?.success) {
      toast.success(res.message || okText);
      reload();
    } else toast.error(hrmsService.responseMessage(res));
  };

  return (
    <>
      {isAdmin ? (
        <div className="mb-3">
          <PrimaryButton onClick={() => setUploading(true)}>Upload document</PrimaryButton>
        </div>
      ) : null}
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No company documents" text={isAdmin ? "Upload policies and forms, then publish them." : "Policies and forms your company shares appear here."} />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  {["Document", "Status", isAdmin ? "Acknowledged" : "Acknowledgement", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600 }}>{d.title}</div>
                      <div style={{ fontSize: 12, color: "#6B7280" }}>{d.description}</div>
                    </td>
                    <td style={tdStyle}>
                      <span style={statusBadge(d.published ? "ACTIVE" : "INACTIVE")}>{d.published ? "Published" : "Draft"}</span>
                    </td>
                    <td style={tdStyle}>
                      {!d.requires_ack ? "—" : isAdmin ? `${d.ack_count} people` : d.acknowledged ? <span style={{ color: "#065F46" }}>Acknowledged</span> : <span style={{ color: "#92400E" }}>Waiting for you</span>}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" style={linkBtn} onClick={() => openSignedUrl(() => hrmsService.getCompanyDocumentUrl(d.id))}>
                        View
                      </button>{" "}
                      {!isAdmin && d.requires_ack && !d.acknowledged ? (
                        <button type="button" style={linkBtn} onClick={() => run(() => hrmsService.acknowledgeCompanyDocument(d.id), "Thank you")}>
                          I have read this
                        </button>
                      ) : null}
                      {isAdmin ? (
                        <>
                          <button type="button" style={linkBtn} onClick={() => run(() => hrmsService.setCompanyDocumentPublished(d.id, !d.published), "Saved")}>
                            {d.published ? "Unpublish" : "Publish"}
                          </button>{" "}
                          {d.requires_ack ? (
                            <button type="button" style={linkBtn} onClick={() => setAcks(d)}>
                              Who has read
                            </button>
                          ) : null}
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <CompanyUpload show={uploading} onHide={() => setUploading(false)} onDone={reload} />
      <Acknowledgements doc={acks} onHide={() => setAcks(null)} />
    </>
  );
}

/* --------------------------- letters (DOC-06) --------------------------- */

function LetterDrawer({ show, onHide, isAdmin, templates, onDone }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  React.useEffect(() => {
    if (show) setValues({});
  }, [show]);
  const save = async () => {
    setSaving(true);
    try {
      const res = isAdmin ? await hrmsService.generateLetter({ employee_id: values.employee_id, template_id: values.template_id }) : await hrmsService.requestLetter({ template_id: values.template_id });
      if (res?.success) {
        toast.success(res.message || "Done");
        onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };
  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title={isAdmin ? "Issue a letter" : "Request a letter"}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving || !values.template_id || (isAdmin && !values.employee_id)}>
            {isAdmin ? "Issue" : "Send request"}
          </PrimaryButton>
        </>
      }
    >
      <FormFields
        values={values}
        onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))}
        fields={[
          ...(isAdmin ? [{ key: "employee_id", label: "Employee", type: "employee", required: true }] : []),
          { key: "template_id", label: "Letter", type: "select", required: true, options: templates.map((t) => ({ label: t.name, value: t.id })) },
        ]}
      />
      {!isAdmin ? <div style={{ fontSize: 12, color: "#6B7280" }}>HR approves the request, then the letter appears here as a PDF.</div> : null}
    </SlidePanel>
  );
}

function Letters({ isAdmin }) {
  const letters = useLoad(() => hrmsService.listLetters(), []);
  const templates = useLoad(() => hrmsService.listLetterTemplates(), []);
  const [open, setOpen] = useState(false);
  const rows = letters.data || [];
  return (
    <>
      <div className="mb-3">
        <PrimaryButton onClick={() => setOpen(true)} disabled={!(templates.data || []).length}>
          {isAdmin ? "Issue a letter" : "Request a letter"}
        </PrimaryButton>
      </div>
      <LoadState loading={letters.loading} error={letters.error} onRetry={letters.reload}>
        {rows.length === 0 ? (
          <EmptyState title="No letters yet" />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 560 }}>
              <thead>
                <tr>
                  {[...(isAdmin ? ["Employee"] : []), "Letter", "Number", "Status", "Date", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((l) => (
                  <tr key={l.id}>
                    {isAdmin ? <td style={tdStyle}>{l.full_name}</td> : null}
                    <td style={tdStyle}>{l.template_name}</td>
                    <td style={tdStyle}>{l.letter_no || "—"}</td>
                    <td style={tdStyle}>
                      <span style={statusBadge(l.status)}>{statusLabel(l.status)}</span>
                    </td>
                    <td style={tdStyle}>{formatDateTime(l.created_at)}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      {l.has_file ? (
                        <button type="button" style={linkBtn} onClick={() => openSignedUrl(() => hrmsService.getLetterUrl(l.id))}>
                          Download
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <LetterDrawer show={open} onHide={() => setOpen(false)} isAdmin={isAdmin} templates={templates.data || []} onDone={letters.reload} />
    </>
  );
}

/* --------------------------- expiring documents (DOC-04) --------------------------- */

const WINDOWS = [
  { label: "30 days", value: 30 },
  { label: "90 days", value: 90 },
  { label: "180 days", value: 180 },
];

function Expiring() {
  const [days, setDays] = useState(90);
  const { data, loading, error, reload } = useLoad(() => hrmsService.getExpiringDocuments(days), [days]);
  const rows = data || [];
  return (
    <>
      <div className="d-flex gap-2 mb-3">
        {WINDOWS.map((w) => (
          <button key={w.value} type="button" className={`btn btn-sm ${days === w.value ? "btn-danger" : "btn-outline-secondary"}`} onClick={() => setDays(w.value)}>
            Next {w.label}
          </button>
        ))}
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="Nothing is about to expire" />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 560 }}>
              <thead>
                <tr>
                  {["Employee", "Document", "Expires", "Days left"].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id}>
                    <td style={tdStyle}>
                      {d.full_name} <span style={{ color: "#6B7280", fontSize: 12 }}>{d.employee_code}</span>
                    </td>
                    <td style={tdStyle}>{d.type_name}</td>
                    <td style={tdStyle}>{formatDate(d.expiry_date)}</td>
                    <td style={{ ...tdStyle, color: d.days_left < 0 ? "#B91C1C" : d.days_left <= 30 ? "#92400E" : "#374151", fontWeight: 600 }}>{d.days_left < 0 ? `Expired ${-d.days_left} days ago` : `${d.days_left} days`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
    </>
  );
}

export default function Documents() {
  const { access } = useHr();
  const isAdmin = hasHrPermission(access, HR_PERMISSIONS.HR_ADMIN);
  const [tab, setTab] = useState("company");
  return (
    <div style={pageStyle}>
      <PageHeader title="Documents & Letters" subtitle="Company policies, HR letters and document expiry" />
      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: "company", label: "Company documents" },
          { key: "letters", label: "Letters" },
          { key: "expiring", label: "Expiring documents" },
        ]}
      />
      {tab === "company" ? <CompanyDocuments isAdmin={isAdmin} /> : null}
      {tab === "letters" ? <Letters isAdmin={isAdmin} /> : null}
      {tab === "expiring" ? <Expiring /> : null}
    </div>
  );
}
